import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, DatePipe, DecimalPipe, JsonPipe, registerLocaleData } from '@angular/common';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import localeDa from '@angular/common/locales/da';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { KatexModule } from 'ng-katex';
import { NgPipesModule } from 'ngx-pipes';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { AppSettingsProvider } from './shared/app-settings.provider';
import { AuthHttpInterceptor } from './shared/auth.http.interceptor';
import { AuthGuard } from './shared/auth.guard';
import { AuthService } from './shared/auth.service';
import { ExpNumber } from './shared/pipes/exp-number.pipe';
import { LoadIndicatorComponent } from './shared/components/load-indicator/load-indicator.component';
import { NumberOrDefaultPipe } from './shared/pipes/number-or-default.pipe';

import {
    CalculationService, CompoundService, CsvService, GeusCalculationService, LocationService, LoggingService,
    NotificationService, PdfmakeService, PreliminaryScreeningService,
    ScreeningService, StatisticsService
} from './services';

import { SafeHtmlPipe } from './components/safehtml.pipe';
import { FilterPipe } from './components/filter.pipe';
import { HomeComponent } from './components/home.component';
import { AuthTokenComponent } from './components/auth-token.component';
import { ClaimsComponent } from './components/claims.component';
import { PageNotFoundComponent } from './components/page-not-found.component';
import { PageAccessDeniedComponent } from './components/page-access-denied.component';

import { SearchLocationComponent } from './components/riskassessment/search-location/search-location.component';
import { SearchCalculationComponent } from './components/riskassessment/search-calculation/search-calculation.component';
import { LocationDetailsComponent } from './components/riskassessment/location-details/location-details.component';
import { SelectModelComponent } from './components/riskassessment/select-model/select-model.component';
import { SelectParametersComponent } from './components/riskassessment/select-parameters/select-parameters.component';
import { CalculationResultComponent } from './components/riskassessment/calculation-result/calculation-result.component';
import { ExternalLocationOverviewComponent } from './components/riskassessment/external-location-overview/external-location-overview.component';

import {
    CompoundDatailsComponent, PreliminaryCompoundDatailsComponent, PreliminaryScreeningLocationDetailsComponent,
    ScreeningLocationDetailsComponent, ScreeningSearchLocationComponent
} from './components/screening';

import { FindDraftComponent } from './components/draft-screening/find-draft/find-draft.component';
import { InitDraftComponent } from './components/draft-screening/init-draft/init-draft.component';
import { StatisticsIndexComponent } from './components/statistics/index.component';
import { PreliminaryLocationDetailsComponent } from './components/riskassessment/preliminary-location-details/preliminary-location-details.component';
import { OAuthModule } from 'angular-oauth2-oidc';

registerLocaleData(localeDa);

@NgModule({
    imports: [
        CommonModule,
        AppRoutingModule,
        BrowserModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule,
        BrowserAnimationsModule,

        OAuthModule.forRoot(),

        KatexModule,
        NgPipesModule
    ],
    declarations: [
        AppComponent,
        AuthTokenComponent,
        ClaimsComponent,
        PageNotFoundComponent,
        PageAccessDeniedComponent,

        HomeComponent,
        SafeHtmlPipe,
        SearchLocationComponent,
        LocationDetailsComponent,
        SelectModelComponent,
        SelectParametersComponent,
        CalculationResultComponent,
        SearchCalculationComponent,

        ExternalLocationOverviewComponent,
        NumberOrDefaultPipe,
        ScreeningSearchLocationComponent,
        ScreeningLocationDetailsComponent,
        CompoundDatailsComponent,
        PreliminaryCompoundDatailsComponent,
        PreliminaryScreeningLocationDetailsComponent,
        StatisticsIndexComponent,
        FindDraftComponent,
        InitDraftComponent,
        ExpNumber,
        LoadIndicatorComponent,
        StatisticsIndexComponent,
        PreliminaryLocationDetailsComponent,

        FilterPipe
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory:
                (provider: AppSettingsProvider, authService: AuthService) => async () => {
                    await provider.loadSettings();
                    await authService.initialize();
                },
            deps: [AppSettingsProvider, AuthService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true
        },
        {
            provide: LOCALE_ID,
            useValue: 'da'
        },
        AuthGuard,
        AuthService,
        LocationService,
        NotificationService,
        CalculationService,
        CompoundService,
        CsvService,
        PdfmakeService,
        ScreeningService,
        PreliminaryScreeningService,
        StatisticsService,
        GeusCalculationService,
        DecimalPipe,
        LoggingService,
        NumberOrDefaultPipe,
        JsonPipe,
        DatePipe
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
